import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import "./Navigation.css";

// Import preload functions
import {
  preloadHome,
  preloadFinancial,
  preloadBrewers,
  preloadEats,
  preloadStore,
} from "../App";

// The logo dot animates in on first load only, so route changes and
// re-renders within a visit don't replay it.
const LOGO_DOT_KEY = "stedelik-logo-dot-played";

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [preloadedRoutes, setPreloadedRoutes] = useState(new Set());
  const [animateLogoDot] = useState(() => {
    try {
      return !window.sessionStorage.getItem(LOGO_DOT_KEY);
    } catch {
      // sessionStorage throws in private mode or with storage blocked
      return true;
    }
  });
  const location = useLocation();

  useEffect(() => {
    if (!animateLogoDot) return;
    try {
      window.sessionStorage.setItem(LOGO_DOT_KEY, "1");
    } catch {
      // nothing to persist to; the dot simply animates again next load
    }
  }, [animateLogoDot]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Preload main routes on mount for instant navigation
  useEffect(() => {
    const preloadMainRoutes = async () => {
      // Preload all main routes after a short delay to not block initial render
      setTimeout(() => {
        preloadHome();
        preloadFinancial();
        preloadBrewers();
        preloadEats();
        preloadStore();
      }, 1000);
    };

    preloadMainRoutes();
  }, []);

  // Preload route on hover for instant navigation
  const handleMouseEnter = (path) => {
    if (preloadedRoutes.has(path)) return;

    const preloadMap = {
      "/": preloadHome,
      "/financial": preloadFinancial,
      "/brewers": preloadBrewers,
      "/eats": preloadEats,
      "/store": preloadStore,
    };

    const preloadFn = preloadMap[path];
    if (preloadFn) {
      preloadFn();
      setPreloadedRoutes((prev) => new Set(prev).add(path));
    }
  };

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/financial", label: "Stedelik Financial" },
    { path: "/brewers", label: "Stedelik Brewers" },
    { path: "/eats", label: "Stedelik Eats" },
    { path: "/store", label: "Stedelik Store" },
  ];

  const handleNavClick = (path) => (e) => {
    if (location.pathname === path) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const handleContactClick = (e) => {
    setMobileMenuOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      const contactSection = document.getElementById("contact");
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    // If not on home page, the Link will navigate to /#contact
    // and Home.js will handle the scrolling
  };

  return (
    <nav className={`navigation ${scrolled ? "scrolled" : ""}`}>
      <div className="container nav-container">
        <Link to="/" className="logo">
          STEDELIK
          <span className={`logo-dot${animateLogoDot ? " animate" : ""}`}>
            .
          </span>
        </Link>

        <div className={`nav-links ${mobileMenuOpen ? "open" : ""}`}>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={location.pathname === link.path ? "active" : ""}
              onClick={handleNavClick(link.path)}
              onMouseEnter={() => handleMouseEnter(link.path)}
              onTouchStart={() => handleMouseEnter(link.path)}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/#contact"
            className="nav-contact"
            onClick={handleContactClick}
          >
            Contact
          </Link>
        </div>

        <button
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </nav>
  );
};

export default Navigation;
